import { useCallback, useMemo } from 'react'

import { NetworkStatus } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { VariablesOf } from '~publish/graphql'

import type { AccessLevel, ProductAccessLevels } from '../types'

export const TEAM_MEMBERS_PAGE_SIZE = 20

export const GetTeamMembers = graphql(/* GraphQL */ `
  query GetTeamMembers($input: TeamMembersInput!) {
    teamMembers(input: $input) {
      edges {
        node {
          id
          name
          email
          organizationRole
          collaborationRole
          isLocked
          isOwner
          createdAt
          account {
            id
            avatar
            tfa {
              type
            }
          }
          channelPermissions {
            productAccessLevels {
              publish
              comments
            }
            channel {
              id
              name
              avatar
              service
            }
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

export interface TeamMember {
  id: string
  name: string
  email: string
  organizationRole: 'admin' | 'member'
  /** Display only; authorize with `allowedActions`. */
  collaborationRole: ReturnType<typeof graphql.scalar<'CollaborationRole'>>
  isLocked: boolean
  isOwner: boolean
  createdAt: string
  account: {
    id: string
    avatar: string
    tfa: { type: 'app' | 'email' } | null
  } | null
  channelPermissions: Array<{
    /**
     * Single-level view of the grant, fed from the `publish` product. Drives
     * the legacy single-dropdown UI (per-product split off).
     */
    accessLevel: AccessLevel
    /** Full per-product grant; drives the per-product UI (split on). */
    productAccessLevels: ProductAccessLevels
    channel: {
      id: string
      name: string
      avatar: string
      service: string
    }
  }>
}

export type TeamMembersFilterInput = NonNullable<
  VariablesOf<typeof GetTeamMembers>['input']['filter']
>

type ErrorCode = 'FORBIDDEN' | 'UNEXPECTED_ERROR' | null

interface UseTeamMembersReturn {
  teamMembers: TeamMember[] | null
  loading: boolean
  loadingMore: boolean
  error: boolean
  errorCode: ErrorCode
  hasNextPage: boolean
  refetch: () => void
  loadMore: () => void
}

export const useTeamMembers = ({
  organizationId,
  search,
  filter,
  enabled = true,
}: {
  organizationId: string
  /**
   * Optional term to filter members by name or email. Filtering happens
   * server-side (before pagination) so it spans the whole team, not just
   * the pages already loaded. Blank/whitespace-only values are treated as
   * "no search".
   */
  search?: string
  /**
   * Optional channel, role and status filters, applied server-side together
   * with `search`. Only honoured for organizations on Roles.
   */
  filter?: TeamMembersFilterInput
  /** Skips the query, e.g. when only pending invitations are shown. */
  enabled?: boolean
}): UseTeamMembersReturn => {
  const trimmedSearch = search?.trim() || undefined
  const input = useMemo(
    () => ({
      organizationId,
      first: TEAM_MEMBERS_PAGE_SIZE,
      ...(trimmedSearch ? { search: trimmedSearch } : {}),
      ...(filter ? { filter } : {}),
    }),
    [organizationId, trimmedSearch, filter],
  )
  const {
    data,
    loading,
    error: queryError,
    refetch,
    fetchMore,
    networkStatus,
  } = useQuery(GetTeamMembers, {
    variables: { input },
    skip: !organizationId || !enabled,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  })

  // A skipped query keeps its last result, so gate the data on `enabled` too.
  const teamMembersData = enabled ? data?.teamMembers : undefined
  const error = enabled ? queryError : undefined

  // Extract team members from edges. Each channel permission carries both the
  // full per-product grant (drives the per-product UI when the split is on) and
  // a single `accessLevel` fed from the `publish` product (drives the legacy
  // single-dropdown UI when the split is off). Memoised so the reference (and
  // each `member` node) stays stable across renders — the edit dialog's
  // form-init effect keys on the `member` it receives, so a fresh array each
  // render would reset in-progress edits.
  const teamMembers = useMemo<TeamMember[] | null>(() => {
    if (!teamMembersData) {
      return null
    }
    return teamMembersData.edges.map((edge) => ({
      ...edge.node,
      channelPermissions: edge.node.channelPermissions.map((permission) => ({
        accessLevel: permission.productAccessLevels.publish,
        productAccessLevels: {
          publish: permission.productAccessLevels.publish,
          comments: permission.productAccessLevels.comments,
        },
        channel: permission.channel,
      })),
    }))
  }, [teamMembersData])

  const loadingMore = networkStatus === NetworkStatus.fetchMore

  const loadMore = useCallback((): void => {
    if (!teamMembersData?.pageInfo.hasNextPage) return

    void fetchMore({
      variables: {
        input: { ...input, after: teamMembersData.pageInfo.endCursor },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.teamMembers?.edges) return prev

        const newEdges = fetchMoreResult.teamMembers.edges
        const newEdgeIds = new Set(newEdges.map((edge) => edge.node.id))

        const existingEdges = (prev.teamMembers?.edges ?? []).filter(
          (edge) => !newEdgeIds.has(edge.node.id),
        )

        return {
          ...prev,
          teamMembers: {
            ...prev.teamMembers,
            edges: [...existingEdges, ...newEdges],
            pageInfo: fetchMoreResult.teamMembers.pageInfo,
          },
        }
      },
    })
  }, [teamMembersData, fetchMore, input])

  const handleRefetch = useCallback((): void => {
    void refetch()
  }, [refetch])

  const rawCode = CombinedGraphQLErrors.is(error)
    ? error.errors[0]?.extensions?.code
    : undefined
  const errorCode: ErrorCode = error
    ? rawCode === 'FORBIDDEN'
      ? 'FORBIDDEN'
      : 'UNEXPECTED_ERROR'
    : null

  if (!teamMembersData) {
    return {
      teamMembers: null,
      loading,
      loadingMore: false,
      error: !!error,
      errorCode,
      hasNextPage: false,
      refetch: handleRefetch,
      loadMore,
    }
  }

  return {
    teamMembers,
    loading,
    loadingMore,
    error: !!error,
    errorCode,
    hasNextPage: teamMembersData.pageInfo.hasNextPage,
    refetch: handleRefetch,
    loadMore,
  }
}
